/**
 * 页面状态管理器
 * 用于管理页面加载状态，避免页面切换时的空白问题
 */

import { ref } from 'vue'

export interface PageState {
  isLoading: boolean
  currentPage: string | null
  lastLoadTime: number
  error: string | null
}

class PageStateManager {
  private _state = ref<PageState>({
    isLoading: false,
    currentPage: null,
    lastLoadTime: 0,
    error: null
  })

  // 页面加载超时时间
  private readonly PAGE_LOAD_TIMEOUT = 3000

  // 页面加载定时器
  private loadingTimers = new Map<string, NodeJS.Timeout>()

  /**
   * 开始页面加载
   */
  startPageLoad(pageName: string): void {
    console.log(`[PageStateManager] 开始加载页面: ${pageName}`)
    
    // 清除之前的定时器
    this.clearLoadingTimer(pageName)
    
    // 设置加载状态
    this._state.value.isLoading = true
    this._state.value.currentPage = pageName
    this._state.value.error = null
    this._state.value.lastLoadTime = Date.now()
    
    // 设置超时保护
    const timer = setTimeout(() => {
      console.warn(`[PageStateManager] 页面加载超时: ${pageName}`)
      this.finishPageLoad(pageName, '页面加载超时')
    }, this.PAGE_LOAD_TIMEOUT)
    
    this.loadingTimers.set(pageName, timer)
  }

  /**
   * 完成页面加载
   */
  finishPageLoad(pageName: string, error?: string): void {
    console.log(`[PageStateManager] 完成页面加载: ${pageName}`, error ? `错误: ${error}` : '成功')
    
    // 清除定时器
    this.clearLoadingTimer(pageName)
    
    // 只有当前页面才更新状态
    if (this._state.value.currentPage === pageName) {
      this._state.value.isLoading = false
      this._state.value.error = error || null
    }
  }

  /**
   * 清除加载定时器
   */
  private clearLoadingTimer(pageName: string): void {
    const timer = this.loadingTimers.get(pageName)
    if (timer) {
      clearTimeout(timer)
      this.loadingTimers.delete(pageName)
    }
  }

  /**
   * 强制重置所有页面状态
   */
  resetAllStates(): void {
    console.log('[PageStateManager] 重置所有页面状态')
    
    // 清除所有定时器
    this.loadingTimers.forEach((timer) => {
      clearTimeout(timer)
    })
    this.loadingTimers.clear()
    
    // 重置状态
    this._state.value.isLoading = false
    this._state.value.currentPage = null
    this._state.value.error = null
  }

  /**
   * 获取当前状态
   */
  get state() {
    return this._state.value
  }

  /**
   * 检查页面是否正在加载
   */
  isPageLoading(pageName?: string): boolean {
    if (pageName) {
      return this._state.value.isLoading && this._state.value.currentPage === pageName
    }
    return this._state.value.isLoading
  }

  /**
   * 获取调试信息
   */
  getDebugInfo() {
    return {
      state: { ...this._state.value },
      activeTimers: Array.from(this.loadingTimers.keys())
    }
  }
}

// 导出单例实例
export const pageStateManager = new PageStateManager()